import { useState } from 'react';
import { closeMonth, reopenMonth, type Answer } from './api';
import type { Month, WarningCode } from './types';

/** What the closing's actions answer: the problem's code, shown through the page's own messages. */
export function useMonthActions(
  month: Month,
  adopt: (month: Month) => void,
  reload: () => void,
  onChanged: () => void,
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (act: () => Promise<Answer<Month>>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const answer = await act();
    setBusy(false);
    if (answer.ok) {
      adopt(answer.value);
      onChanged();
      return true;
    }
    setError(answer.problem.code === '' ? 'other' : answer.problem.code);
    // The blockers or the warnings changed under the reader: show them as they are now.
    if (['CLOSING_BLOCKED', 'WARNINGS_NOT_CONFIRMED'].includes(answer.problem.code)) reload();
    return false;
  };

  const warningCodes = [...new Set(month.warnings.map((issue) => issue.code))] as WarningCode[];
  return {
    busy,
    error,
    clearError: () => setError(null),
    close: () => run(() => closeMonth(month.month, warningCodes)),
    reopen: (reason: string) => run(() => reopenMonth(month.month, reason)),
  } as const;
}
